/**
 * Brings a local document and its copy in the cloud to the same value (cross-save). The order of
 * the steps is the whole point, so it is written once, here, with the network and the storage
 * given in: read the cloud copy, merge it with the local one, keep the local copy up to date,
 * and write the cloud copy back only if it changed, against the version that was read. If another
 * device saved in between, the write is refused as a conflict and the whole thing starts again from
 * the read, up to a few times.
 */

export interface Remote {
  data: unknown;
  version: number;
}

export type ReadResult = { ok: true; remote: Remote | null } | { ok: false };
export type WriteResult = { ok: true; version: number } | { ok: false; conflict: boolean };

export interface SyncPorts<T> {
  readLocal: () => T;
  readRemote: () => Promise<ReadResult>;
  /** Null when the cloud copy is not a document this version of the app understands. */
  parse: (raw: unknown) => T | null;
  merge: (local: T, remote: T | null) => T;
  equal: (a: T, b: T) => boolean;
  writeRemote: (data: T, expectedVersion: number | null) => Promise<WriteResult>;
  /** Called with the merged value when it differs from the local one. */
  applyLocal: (merged: T) => void;
}

export type SyncOutcome =
  | { status: 'synced'; pulled: boolean; pushed: boolean; attempts: number }
  | { status: 'failed'; reason: 'read' | 'write' | 'conflicts' | 'unreadable'; attempts: number };

export async function syncDocument<T>(ports: SyncPorts<T>, maxAttempts = 4): Promise<SyncOutcome> {
  let pulled = false;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const read = await ports.readRemote();
    if (!read.ok) return { status: 'failed', reason: 'read', attempts: attempt };

    const local = ports.readLocal();
    let remoteValue: T | null = null;
    if (read.remote) {
      remoteValue = ports.parse(read.remote.data);
      // A cloud copy this version cannot read (a newer app wrote it): never write over it.
      if (remoteValue === null)
        return { status: 'failed', reason: 'unreadable', attempts: attempt };
    }

    const merged = ports.merge(local, remoteValue);
    if (!ports.equal(merged, local)) {
      ports.applyLocal(merged);
      pulled = true;
    }
    const needsPush = remoteValue === null || !ports.equal(merged, remoteValue);
    if (!needsPush) return { status: 'synced', pulled, pushed: false, attempts: attempt };

    const written = await ports.writeRemote(merged, read.remote?.version ?? null);
    if (written.ok) return { status: 'synced', pulled, pushed: true, attempts: attempt };
    if (!written.conflict) return { status: 'failed', reason: 'write', attempts: attempt };
  }
  return { status: 'failed', reason: 'conflicts', attempts: maxAttempts };
}
